import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {
  SocialSecurityNumber,
  ValidSocialSecurityNumber,
  EmptySocialSecurityNumber,
  isValidSocialSecurityNumber,
  UnvalidSocialSecurityNumber,
} from 'ApplicationEntry/models/SocialSecurityNumber';
import {PhoneNumber, ValidPhoneNumber, EmptyPhoneNumber, isValidPhoneNumber} from 'ApplicationEntry/models/PhoneNumber';

import {StringUnion, NonEmptyString, EmptyString} from 'Core/models/EmptyString';
import {ZipCode, ValidZipCode, isValidZipCode, EmptyZipCode, UnvalidatedZipCode} from 'ApplicationEntry/models/ZipCode';
import {Sex} from 'ApplicationEntry/models/Sex';
import {isValidDate} from 'ApplicationEntry/models/Date';

export const relations = ['Child', 'Step-Child', 'Spouse', 'Grandchild', 'Domestic Partner'];

export enum DependentActionType {
  ADD_DEPENDENT_REDUCER = 'dependents/ADD_DEPENDENT_REDUCER',
  REMOVE_DEPENDENT_REDUCER = 'dependents/REMOVE_DEPENDENT_REDUCER',
  EMPTY_DEPENDENT_REDUCER = 'dependents/EMPTY_DEPENDENT_REDUCER',
  UPDATE_FIRST_NAME_REDUCER = 'dependents/UPDATE_FIRST_NAME_REDUCER',
  UPDATE_MIDDLE_NAME_REDUCER = 'dependents/UPDATE_MIDDLE_NAME_REDUCER',
  UPDATE_LAST_NAME_REDUCER = 'dependents/UPDATE_LAST_NAME_REDUCER',
  UPDATE_RELATIONSHIP_REDUCER = 'dependents/UPDATE_RELATIONSHIP_REDUCER',
  UPDATE_SEX_REDUCER = 'dependents/UPDATE_SEX_REDUCER',
  UPDATE_SSN_REDUCER = 'dependents/UPDATE_SSN_REDUCER',
  UPDATE_DOB_REDUCER = 'dependents/UPDATE_DOB_REDUCER',
  UPDATE_PHONE_REDUCER = 'dependents/UPDATE_PHONE_REDUCER',
  UPDATE_ADDRESS_REDUCER = 'dependents/UPDATE_ADDRESS_REDUCER',
  UPDATE_ADDRESS_LINE2_REDUCER = 'dependents/UPDATE_ADDRESS_LINE2_REDUCER',
  UPDATE_CITY_REDUCER = 'dependents/UPDATE_CITY_REDUCER',
  UPDATE_STATE_REDUCER = 'dependents/UPDATE_STATE_REDUCER',
  UPDATE_ZIP_REDUCER = 'dependents/UPDATE_ZIP_REDUCER',
  EDIT_DEPENDENT_REDUCER = 'dependents/EDIT_DEPENDENT_REDUCER',
}

export class UnvalidatedDependents {
  public readonly key = 'UnvalidatedDependents';

  constructor(
    public readonly firstName: StringUnion,
    public readonly middleName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly relationship: StringUnion,
    public readonly sex: Sex | '',
    public readonly socialSecurityNumber: SocialSecurityNumber,
    public readonly dob: Date | '',
    public readonly phone: PhoneNumber,
    public readonly address: StringUnion,
    public readonly addressLine2: string,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode,
    public readonly occSmokerCode: StringUnion,
    public readonly accidentDependent: boolean,
    public readonly cancerDependent: boolean,
    public readonly shopDependent: boolean
  ) {}
}

export class InvalidDependents {
  public readonly key = 'InvalidDependents';

  constructor(
    public readonly firstName: StringUnion,
    public readonly middleName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly relationship: StringUnion,
    public readonly sex: Sex | '',
    public readonly socialSecurityNumber: SocialSecurityNumber,
    public readonly dob: Date | '',
    public readonly phone: PhoneNumber,
    public readonly address: StringUnion,
    public readonly addressLine2: string,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode,
    public readonly occSmokerCode: StringUnion,
    public readonly accidentDependent: boolean,
    public readonly cancerDependent: boolean,
    public readonly shopDependent: boolean
  ) {}
}

type AppProps = {
  firstName: StringUnion;
  middleName: StringUnion;
  lastName: StringUnion;
  relationship: StringUnion;
  sex: Sex | '';
  socialSecurityNumber: SocialSecurityNumber;
  dob: Date | '';
  phone: PhoneNumber;
  address: StringUnion;
  addressLine2: string;
  city: StringUnion;
  state: StringUnion;
  zip: ZipCode;
  occSmokerCode: StringUnion;
  accidentDependent: boolean;
  cancerDependent: boolean;
  shopDependent: boolean;
};

export class ValidDependents {
  public static createFromApp({
    firstName,
    middleName,
    lastName,
    relationship,
    sex,
    socialSecurityNumber,
    dob,
    phone,
    address,
    addressLine2,
    city,
    state,
    zip,
    occSmokerCode,
    accidentDependent,
    cancerDependent,
    shopDependent,
  }: AppProps) {
    return this.create(
      firstName,
      middleName,
      lastName,
      relationship,
      sex,
      socialSecurityNumber,
      dob,
      phone,
      address,
      addressLine2,
      city,
      state,
      zip,
      occSmokerCode,
      accidentDependent,
      cancerDependent,
      shopDependent
    );
  }

  public static create(
    firstName: StringUnion,
    middleName: StringUnion,
    lastName: StringUnion,
    relationship: StringUnion,
    sex: Sex | '',
    socialSecurityNumber: SocialSecurityNumber,
    dob: Date | '',
    phone: PhoneNumber,
    address: StringUnion,
    addressLine2: string,
    city: StringUnion,
    state: StringUnion,
    zip: ZipCode,
    occSmokerCode: StringUnion,
    accidentDependent: boolean,
    cancerDependent: boolean,
    shopDependent: boolean
  ) {
    return isValidSocialSecurityNumber(socialSecurityNumber) &&
      isValidPhoneNumber(phone) &&
      isValidZipCode(zip) &&
      isValidDate(dob) &&
      !(
        firstName.value === '' &&
        middleName.value === '' &&
        lastName.value === '' &&
        relationship.value === '' &&
        sex === '' &&
        socialSecurityNumber.value === '' &&
        dob === '' &&
        phone.phoneNumber === '' &&
        address.value === '' &&
        addressLine2 === '' &&
        city.value === '' &&
        state.value === '' &&
        zip.value === ''
      )
      ? new ValidDependents(
          firstName,
          middleName,
          lastName,
          relationship,
          sex,
          socialSecurityNumber,
          dob,
          phone,
          address,
          addressLine2,
          city,
          state,
          zip,
          occSmokerCode,
          accidentDependent,
          cancerDependent,
          shopDependent
        )
      : new InvalidDependents(
          firstName,
          middleName,
          lastName,
          relationship,
          sex,
          socialSecurityNumber,
          dob,
          phone,
          address,
          addressLine2,
          city,
          state,
          zip,
          occSmokerCode,
          accidentDependent,
          cancerDependent,
          shopDependent
        );
  }

  public readonly key = 'ValidDependents';
  private constructor(
    public readonly firstName: StringUnion,
    public readonly middleName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly relationship: StringUnion,
    public readonly sex: Sex | '',
    public readonly socialSecurityNumber: ValidSocialSecurityNumber | UnvalidSocialSecurityNumber,
    public readonly dob: Date | '',
    public readonly phone: ValidPhoneNumber,
    public readonly address: StringUnion,
    public readonly addressLine2: string,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ValidZipCode | UnvalidatedZipCode,
    public readonly occSmokerCode: StringUnion,
    public readonly accidentDependent: boolean,
    public readonly cancerDependent: boolean,
    public readonly shopDependent: boolean
  ) {}
}

export type Dependents = UnvalidatedDependents | InvalidDependents | ValidDependents;

export type DependentsOptions<TReturn> = ToPartialMatchOptions<Dependents, 'key', TReturn>;

export const matchDependents = <TReturn>(options: DependentsOptions<TReturn>) => (app: Dependents): TReturn => {
  switch (app.key) {
    case 'InvalidDependents':
      return options.InvalidDependents ? options.InvalidDependents(app) : options.default(app);
    case 'UnvalidatedDependents':
      return options.UnvalidatedDependents ? options.UnvalidatedDependents(app) : options.default(app);
    case 'ValidDependents':
      return options.ValidDependents ? options.ValidDependents(app) : options.default(app);
  }
};

export const changeFirstName = (firstName: string) => (app: Dependents) =>
  ValidDependents.createFromApp({
    ...app,
    firstName: NonEmptyString.create(firstName),
  });

export const changeMiddleName = (middleName: string) => (app: Dependents) =>
  ValidDependents.createFromApp({
    ...app,
    middleName: NonEmptyString.create(middleName),
  });

export const changeLastName = (lastName: string) => (app: Dependents) =>
  ValidDependents.createFromApp({
    ...app,
    lastName: NonEmptyString.create(lastName),
  });

export const changeRelationship = (relationship: string) => (app: Dependents) =>
  ValidDependents.createFromApp({
    ...app,
    relationship: NonEmptyString.create(relationship),
  });

export const changeSex = (sex: Sex) => (app: Dependents) =>
  ValidDependents.createFromApp({
    ...app,
    sex: sex,
  });

export const changeSSN = (socialSecurityNumber: string) => (app: Dependents) =>
  ValidDependents.createFromApp({
    ...app,
    socialSecurityNumber: ValidSocialSecurityNumber.create(socialSecurityNumber),
  });

export const changeDOB = (dob: Date | '') => (app: Dependents) =>
  ValidDependents.createFromApp({
    ...app,
    dob: dob,
  });

export const changePhone = (phone: PhoneNumber) => (app: Dependents) =>
  ValidDependents.createFromApp({
    ...app,
    phone: phone,
  });

export const changeAddress = (address: string) => (app: Dependents) =>
  ValidDependents.createFromApp({
    ...app,
    address: NonEmptyString.create(address),
  });

export const changeAddressLine2 = (addressLine2: string) => (app: Dependents) =>
  ValidDependents.createFromApp({
    ...app,
    addressLine2: addressLine2,
  });

export const changeCity = (city: string) => (app: Dependents) =>
  ValidDependents.createFromApp({
    ...app,
    city: NonEmptyString.create(city),
  });

export const changeState = (state: string) => (app: Dependents) =>
  ValidDependents.createFromApp({
    ...app,
    state: NonEmptyString.create(state),
  });

export const changeZip = (zip: ZipCode) => (app: Dependents) =>
  ValidDependents.createFromApp({
    ...app,
    zip,
  });

export const changeOccSmokerCode = (occSmokerCode: string) => (app: ValidDependents) =>
  ValidDependents.createFromApp({...app, occSmokerCode: NonEmptyString.create(occSmokerCode)});

export const updateDependentValues = (
  firstName: StringUnion,
  middleName: StringUnion,
  lastName: StringUnion,
  relationship: StringUnion,
  sex: Sex | '',
  socialSecurityNumber: string,
  dob: Date | '',
  phone: PhoneNumber,
  address: StringUnion,
  addressLine2: string,
  city: StringUnion,
  state: StringUnion,
  zip: ZipCode,
  occSmokerCode: StringUnion,
  accidentDependent: boolean,
  cancerDependent: boolean,
  shopDependent: boolean
) => () =>
  ValidDependents.create(
    firstName,
    middleName,
    lastName,
    relationship,
    sex,
    ValidSocialSecurityNumber.create(socialSecurityNumber),
    dob,
    phone,
    address,
    addressLine2,
    city,
    state,
    zip,
    occSmokerCode,
    accidentDependent,
    cancerDependent,
    shopDependent
  );

export const EmptyDependents = new UnvalidatedDependents(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  '',
  EmptySocialSecurityNumber,
  '',
  EmptyPhoneNumber,
  new EmptyString(),
  '',
  new EmptyString(),
  new EmptyString(),
  EmptyZipCode,
  new EmptyString(),
  false,
  false,
  false
);

export const isValidDependents = (ele: Dependents) => {
  return ele.key === 'InvalidDependents' || ele.key === 'UnvalidatedDependents';
};
